import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3 } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

const TOTAL_SCREENS = 21;

const SCREEN_NAMES: Record<number, string> = {
  1: 'Intro + Leeftijd',
  2: 'Verbeteren',
  3: 'Rimpels',
  4: 'Waar werken',
  5: 'Social proof',
  6: 'Zon',
  7: 'Slaap',
  8: 'Stress',
  9: 'Huidtint',
  10: 'Gevoeligheid',
  11: 'Match + Loader',
  12: 'Huidprofiel',
  13: 'Eerdere oplossingen',
  14: 'Info',
  15: 'Tijd',
  16: 'Info 2',
  17: 'Gevoel',
  18: 'Stralende versie',
  19: 'Gelegenheid',
  20: 'Resultaten klaar',
  21: 'Resultaten',
};

interface Session {
  current_step: number;
  is_completed: boolean;
}

const LiveAfhaakFunnel = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageviewCount, setPageviewCount] = useState(0);

  const fetchData = async () => {
    const [sessData, pvData] = await Promise.all([
      fetchAllRows<{ current_step: number | null; is_completed: boolean | null }>('quiz_sessions', 'current_step, is_completed', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
      fetchAllRows<{ id: string }>('quiz_pageviews', 'id', [
        { column: 'utm_source', operator: 'not.is', value: null },
      ]),
    ]);
    setSessions(sessData.map(s => ({
      current_step: s.current_step ?? 1,
      is_completed: s.is_completed ?? false,
    })));
    setPageviewCount(pvData.length);
  };

  useEffect(() => {
    fetchData();

    const channel = supabase
      .channel('live-afhaak-funnel')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchData())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Calculate funnel data
  // Screen 1 "Bereikt" = pageviews (first thing that happens)
  // Screen 2+ "Bereikt" = sessions where current_step >= screen
  const funnelData = [];
  for (let screen = 1; screen <= TOTAL_SCREENS; screen++) {
    const bereikt = screen === 1
      ? pageviewCount
      : sessions.filter(s => s.current_step >= screen).length;
    
    // Afgehaakt = sessions currently ON this screen and NOT completed
    const afgehaakt = screen === 1
      ? Math.max(0, pageviewCount - sessions.filter(s => s.current_step >= 2).length)
      : sessions.filter(s => s.current_step === screen && !s.is_completed).length;
    
    const afhaakPct = bereikt > 0 ? ((afgehaakt / bereikt) * 100).toFixed(1) : '0';

    funnelData.push({
      screen,
      name: SCREEN_NAMES[screen] || `Scherm ${screen}`,
      bereikt,
      afgehaakt,
      afhaakPct,
    });
  }

  const completedCount = sessions.filter(s => s.is_completed).length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4" />
        Afhaak Funnel
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-800 bg-gray-900 text-white">
              <th className="text-left py-2 px-3 font-semibold">Scherm</th>
              <th className="text-right py-2 px-3 font-semibold">Bereikt</th>
              <th className="text-right py-2 px-3 font-semibold">Afgehaakt</th>
              <th className="text-right py-2 px-3 font-semibold">Afhaak %</th>
            </tr>
          </thead>
          <tbody>
            {funnelData.map((step) => (
              <tr key={step.screen} className={`border-b border-gray-100 ${step.afgehaakt > 0 ? 'bg-red-50/40' : ''}`}>
                <td className="py-2 px-3 font-medium text-gray-700">
                  <span className="text-gray-400 mr-1">{step.screen}.</span>
                  {step.name}
                </td>
                <td className="py-2 px-3 text-right text-gray-900 font-medium">{step.bereikt}</td>
                <td className="py-2 px-3 text-right text-gray-600">{step.afgehaakt}</td>
                <td className="py-2 px-3 text-right">
                  <span className={step.afgehaakt > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>
                    {step.afhaakPct}%
                  </span>
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-emerald-200 bg-emerald-50/60">
              <td className="py-2 px-3 font-medium text-emerald-700">✅ Voltooid</td>
              <td className="py-2 px-3 text-right text-emerald-700 font-bold">{completedCount}</td>
              <td className="py-2 px-3 text-right text-gray-400">—</td>
              <td className="py-2 px-3 text-right text-gray-400">—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LiveAfhaakFunnel;
